import * as React from "react";

import type {
    DUPRAppEligibility,
    JourneyMetadataRolloutPhase,
} from "@volley/data";

import fetchApi, { logFetchError } from "../../../util/fetchApi";
import { useSelectedSport } from "../../common/context/sport";

export interface PadelRolloutPhaseValue {
    phase: JourneyMetadataRolloutPhase;
    /**
     * True while the answer could still change. Consumers that would otherwise
     * render the general-release presentation must withhold rather than show it
     * and correct themselves — this is the same flash `HomeDuprValue.loading`
     * exists to prevent, with the added cost that the wrong presentation here
     * offers a dashboard that redirects.
     */
    loading: boolean;
}

/**
 * The padel assessment's rollout phase for this environment.
 *
 * Read from the assessment eligibility endpoint, which resolves it from the
 * sport's journeys — deliberately NOT from a feature flag. Features are granted
 * per role and per segment (`usersRepo.getFeatures`), so a flag would let an
 * admin or an assessor sit in a different phase from the players they are
 * running the trial for: rating optional for them, required for everyone else,
 * with nothing anywhere reporting the split. The phase is a property of the
 * environment's content, and journey metadata is where content lives.
 *
 * Defaults to "trial" while loading and on failure, so every surface fails
 * closed in the same direction.
 */
export default function usePadelRolloutPhase(): PadelRolloutPhaseValue {
    const { selected: sport } = useSelectedSport();
    // Only padel players have any use for this, so nobody else pays for the
    // request. Mirrors `HomeDuprProvider`'s sport gate.
    const shouldFetch = sport === "PADEL";

    const [phase, setPhase] =
        React.useState<JourneyMetadataRolloutPhase>("trial");
    // Starts true so consumers withhold rather than flash: the sport context
    // reports a placeholder before it resolves, so "not padel" is not yet a
    // settled answer on the first render.
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        if (!shouldFetch) {
            setPhase("trial");
            setLoading(false);
            return undefined;
        }

        const controller = new AbortController();
        setLoading(true);
        fetchApi<DUPRAppEligibility>(
            "/api/journeys/long-form?sport=PADEL",
            "GET",
            undefined,
            { signal: controller.signal },
        )
            .then((res) => {
                setPhase(res.rolloutPhase);
                setLoading(false);
            })
            .catch((err) => {
                if (controller.signal.aborted) return;
                // Degrades to trial rather than surfacing an error: this gates
                // presentation, and the strict phase is the safe presentation.
                logFetchError(err, "Failed to load padel rollout phase");
                setPhase("trial");
                setLoading(false);
            });

        return () => {
            controller.abort();
        };
    }, [shouldFetch]);

    return { phase, loading };
}
